import { useFormState, useValidate } from '@flywire/react-hooks';
import { fetchSender } from 'actions/entities/sender/sender';
import { setDisplayCoupon, setFieldValue } from 'actions/ui/ui';
import {
  COUPON_FIELD,
  IS_RECURRING_FIELD,
  OFFER_FIELD,
  TERMS_FIELD,
} from 'constants/fields';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { getOfferFields } from 'selectors/entities/fields/fields';
import { getSelectedOffer } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';
import { constraints as constraintsCV } from 'utils/clientValidate/constraints';

type UsePaymentOptionsProps = {
  selectOption: (name: string, value: string) => void;
  onClick: () => void;
};

export const usePaymentOptions = ({
  onClick,
  selectOption,
}: UsePaymentOptionsProps) => {
  const offerFields = useSelector(getOfferFields);
  const selectedOption = useSelector(getSelectedOffer);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const offerFieldValues = offerFields.reduce(
    (acc, field) => ({
      ...acc,
      ...(field.defaultValue && { [field.id]: field.defaultValue }),
    }),
    {},
  );

  const { values, dirtyFields, update } = useFormState({
    [TERMS_FIELD]: false,
    [COUPON_FIELD]: false,
    ...(selectedOption?.id && { [OFFER_FIELD]: selectedOption.id }),
    ...offerFieldValues,
  });

  const constraints = {
    ...baseConstraints,
    ...constraintsCV.generate({
      fieldOrFields: offerFields,
    }),
  };

  const { isValid, errors } = useValidate(values, constraints);

  async function handleSelectOffer(id: string, isRecurring: boolean = false) {
    update(OFFER_FIELD, id);
    selectOption(OFFER_FIELD, id);
    dispatch(setFieldValue(IS_RECURRING_FIELD, isRecurring));
    await dispatch(fetchSender());
  }

  function handleFieldChange(name: string, value: string) {
    update(name, value);
    dispatch(setFieldValue(name, value));
  }

  function handleCouponChange() {
    update(COUPON_FIELD, !values.coupon);
    dispatch(setDisplayCoupon(!values.coupon));
  }

  function handleTermsChange() {
    update(TERMS_FIELD, !values.terms);
  }

  const resetSubmitting = () => {
    setIsSubmitting(false);
  };

  async function handleOnClick() {
    setIsSubmitting(true);
    try {
      await onClick();
    } catch {
      resetSubmitting();
    }
  }

  return {
    isValid,
    errors,
    values,
    dirtyFields,
    isSubmitting,
    selectedOption,
    handleOnClick,
    handleTermsChange,
    handleCouponChange,
    handleFieldChange,
    handleSelectOffer,
    resetSubmitting,
  };
};

const baseConstraints = {
  [TERMS_FIELD]: {
    presence: true,
    inclusion: {
      within: [true],
    },
  },
  [OFFER_FIELD]: {
    presence: true,
  },
};
